import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { useAdminEnums } from '../../../../api/enums'
import { useAdminMergeFields } from '../../../../api/merge-fields'
import { AdminPdfField, EnumOption, FormFieldLinkOption } from '../../../../api/types'
import { useCreateAdminPdfField, useUpdateAdminPdfField } from '../../../../api/pdf-fields'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'

// Sentinel for "no linked form field" — an empty referenceID can never collide with a real field.
const NONE_LINKED_FIELD: FormFieldLinkOption = { referenceID: '', label: 'None – no linked field' }

// The literal strings a PDF checkbox mapping actually needs (FieldData.CB_Checked/CB_Unchecked resolve to
// these, not to the constant names themselves) and what a linked boolean/checkbox field's own answer
// resolves to when compared against — same two strings, different framing depending on which input it's for.
const CHECKBOX_VALUE_OPTIONS: EnumOption[] = [
  { value: 'true', label: 'Checked' },
  { value: 'false', label: 'Unchecked' },
]
const YES_NO_VALUE_OPTIONS: EnumOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

interface PdfFieldMappingValues {
  formFieldReferenceID: string
  defaultData: string
  isRecipientField: boolean
  primaryOnly: boolean
  isConditional: boolean
  conditionalData: string
  conditionalValue: string
  excludeFromPreview: boolean
  redactable: boolean
}

const pdfFieldMappingSchema = yup.object({
  formFieldReferenceID: yup.string().optional().default(''),
  // A mapping with neither a linked field nor a default writes nothing to the PDF, yet would still show as
  // "Mapped" — require a default whenever there's no link, and whenever conditional (the condition only
  // gates applying the default, so a conditional mapping with no default is a no-op even when it matches).
  defaultData: yup
    .string()
    .optional()
    .default('')
    .when(['formFieldReferenceID', 'isConditional'], {
      is: (formFieldReferenceID: string, isConditional: boolean) => !formFieldReferenceID || isConditional,
      then: (schema) => schema.required('Enter a default value, or link a form field instead'),
    }),
  isRecipientField: yup.boolean().default(false),
  primaryOnly: yup.boolean().default(false),
  isConditional: yup.boolean().default(false),
  // Ticking "Apply Default Value Conditionally" without actually filling these in used to save silently
  // as unconditional (the submit handler coerces a blank value to null) — require them instead.
  conditionalData: yup
    .string()
    .optional()
    .default('')
    .when(['isConditional', 'formFieldReferenceID'], {
      is: (isConditional: boolean, formFieldReferenceID: string) => isConditional && !formFieldReferenceID,
      then: (schema) => schema.required('Enter a condition value or merge token'),
    }),
  conditionalValue: yup
    .string()
    .optional()
    .default('')
    .when('isConditional', {
      is: true,
      then: (schema) => schema.required('Enter the value to match'),
    }),
  excludeFromPreview: yup.boolean().default(false),
  redactable: yup.boolean().default(false),
})

interface PdfFieldMappingDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  /** The AcroForm field name this mapping fills — fixed, comes from PDF extraction, never edited here. */
  pdfFieldKey: string
  /** The existing mapping for this key, or null to create a new one. */
  mapping: AdminPdfField | null
  formFieldOptions: FormFieldLinkOption[]
  onSaved: () => void
}

/** Add/Edit drawer for a PDF field mapping. Creates (POST) or updates (PUT) then triggers a refresh. */
export const PdfFieldMappingDrawer: React.FC<PdfFieldMappingDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  pdfFieldKey,
  mapping,
  formFieldOptions,
  onSaved,
}) => {
  const formId = 'pdf-field-mapping-form'
  const { data: mergeFields } = useAdminMergeFields()
  const { data: enumCatalog } = useAdminEnums()
  const [createMapping, creating] = useCreateAdminPdfField(formReferenceID, versionReferenceID)
  const [updateMapping, updating] = useUpdateAdminPdfField(
    formReferenceID,
    versionReferenceID,
    mapping?.referenceID ?? '',
  )
  const isBusy = creating || updating
  // Custom mapping mode — pdfFieldKey prop is empty, admin types their own key.
  const isCustomMode = !pdfFieldKey
  const [customKey, setCustomKey] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PdfFieldMappingValues>({
    resolver: yupResolver(pdfFieldMappingSchema),
    defaultValues: {
      formFieldReferenceID: '',
      defaultData: '',
      isRecipientField: false,
      primaryOnly: false,
      isConditional: false,
      conditionalData: '',
      conditionalValue: '',
      excludeFromPreview: false,
      redactable: false,
    },
  })

  const [linkedFieldOption, setLinkedFieldOption] = useState<FormFieldLinkOption>(NONE_LINKED_FIELD)
  const [defaultIsMergeField, setDefaultIsMergeField] = useState(false)
  const [defaultIsCheckbox, setDefaultIsCheckbox] = useState(false)
  const [conditionIsMergeField, setConditionIsMergeField] = useState(false)
  const [matchIsEnumValue, setMatchIsEnumValue] = useState(false)
  const [matchIsYesNo, setMatchIsYesNo] = useState(false)

  const linkedFieldOptions: FormFieldLinkOption[] = useMemo(() => {
    const options: FormFieldLinkOption[] = [NONE_LINKED_FIELD, ...formFieldOptions]
    const linkedRef = mapping?.formFieldReferenceID
    // If the linked field was deleted (or formFieldOptions is stale from another session), fall back to a
    // placeholder so the Select shows what's actually saved instead of silently defaulting to "None".
    if (linkedRef && !options.some((option) => option.referenceID === linkedRef)) {
      options.push({ referenceID: linkedRef, label: `Missing field (${linkedRef})` })
    }
    return options
  }, [formFieldOptions, mapping?.formFieldReferenceID])
  const mergeFieldOptions: EnumOption[] = (mergeFields ?? []).map((mergeField) => ({
    value: mergeField.token,
    label: `${mergeField.category} · ${mergeField.name}`,
  }))

  // "Value to Match" has to equal the exact display text an enum-backed answer renders as (e.g. "6 months"),
  // not its code name — flattened across every enum so the admin can pick it from the same catalog the rest
  // of the builder's enum dropdowns use, instead of having to know/guess it. Stored value = the display text
  // itself (what conditionalValue is actually compared against), not the enum member name.
  const enumValueOptions: EnumOption[] = useMemo(
    () =>
      Object.entries(enumCatalog ?? {}).flatMap(([enumName, options]) => {
        return Array.isArray(options)
          ? options.map((option) => ({ value: option.label, label: `${enumName} · ${option.label}` }))
          : []
      }),
    [enumCatalog],
  )

  // Reset the form to the mapping's values (or blank create defaults) each time the drawer opens.
  useEffect(() => {
    if (!isOpen) return
    setCustomKey(mapping?.pdfFieldKey ?? '')
    reset({
      formFieldReferenceID: mapping?.formFieldReferenceID ?? '',
      defaultData: mapping?.defaultData ?? '',
      isRecipientField: mapping?.isRecipientField ?? false,
      primaryOnly: mapping?.primaryOnly ?? false,
      isConditional: Boolean(mapping?.conditionalValue),
      conditionalData: mapping?.conditionalData ?? '',
      conditionalValue: mapping?.conditionalValue ?? '',
      excludeFromPreview: mapping?.excludeFromPreview ?? false,
      redactable: mapping?.redactable ?? false,
    })
    setDefaultIsMergeField(Boolean(mapping?.defaultData?.startsWith('{{')))
    setDefaultIsCheckbox(mapping?.defaultData === 'true' || mapping?.defaultData === 'false')
    setConditionIsMergeField(Boolean(mapping?.conditionalData?.startsWith('{{')))
    setMatchIsEnumValue(false)
    setMatchIsYesNo(mapping?.conditionalValue === 'true' || mapping?.conditionalValue === 'false')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mapping])

  useEffect(() => {
    if (!isOpen) return
    const linkedRef = mapping?.formFieldReferenceID ?? ''
    setLinkedFieldOption(linkedFieldOptions.find((option) => option.referenceID === linkedRef) ?? NONE_LINKED_FIELD)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mapping, formFieldOptions])

  const handleClose = () => onOpenChange(false)

  const isLinked = Boolean(watch('formFieldReferenceID'))
  const isConditional = watch('isConditional')
  const isRecipientField = watch('isRecipientField')

  const onSubmit: SubmitHandler<PdfFieldMappingValues> = async (values) => {
    const resolvedKey = isCustomMode ? customKey : pdfFieldKey
    if (isCustomMode && !resolvedKey.trim()) return
    const payload = {
      pdfFieldKey: resolvedKey,
      formFieldReferenceID: values.formFieldReferenceID || null,
      defaultData: values.defaultData || null,
      isRecipientField: values.isRecipientField,
      primaryOnly: values.isRecipientField ? values.primaryOnly : false,
      // The condition compares against the linked field's own answer when one is linked, so conditionalData
      // (a merge token to compare instead) only applies when there's nothing linked.
      conditionalValue: values.isConditional ? values.conditionalValue || null : null,
      conditionalData: values.isConditional && !values.formFieldReferenceID ? values.conditionalData || null : null,
      excludeFromPreview: values.excludeFromPreview,
      redactable: values.redactable,
    }

    try {
      if (mapping) await updateMapping(payload)
      else await createMapping(payload)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={isCustomMode && !mapping ? 'Add Custom Map' : mapping ? 'Edit PDF Field Mapping' : 'Add PDF Field Mapping'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          {isCustomMode ? (
            <InputGroup
              label="PDF Field"
              value={customKey}
              onChange={(e) => setCustomKey((e.target as HTMLInputElement).value)}
              errorMessage={!customKey.trim() ? 'Enter a PDF field key' : undefined}
            />
          ) : (
            <InputGroup label="PDF Field" value={pdfFieldKey} disabled />
          )}

          <div>
            <Select
              label="Linked Form Field"
              options={linkedFieldOptions}
              value={linkedFieldOption}
              getOptionLabel={(option: FormFieldLinkOption) => option.label}
              getOptionValue={(option: FormFieldLinkOption) => option.referenceID}
              onChange={(option: FormFieldLinkOption | null) => {
                const next = option ?? NONE_LINKED_FIELD
                setLinkedFieldOption(next)
                setValue('formFieldReferenceID', next.referenceID)
                // conditionalData only applies when nothing's linked (see onSubmit) — clear it once a field
                // is linked so a stale value can't resurface if the user unlinks again later.
                if (next.referenceID) setValue('conditionalData', '')
              }}
            />
            <FieldHelp>
              The agent&apos;s answer to this field is written straight onto the PDF. Leave as None to always use the
              Default Value below instead.
            </FieldHelp>
          </div>

          <Checkbox
            label="Default is a merge field"
            supplementaryInfo="Pre-fill from a merge token (e.g. today's date) instead of static text."
            checked={defaultIsMergeField}
            onChange={(event) => {
              const checked = (event.target as HTMLInputElement).checked
              setDefaultIsMergeField(checked)
              setDefaultIsCheckbox(false)
              setValue('defaultData', '')
            }}
          />
          <Checkbox
            label="Default writes a PDF checkbox"
            supplementaryInfo="Turn on when the PDF field being mapped is an AcroForm checkbox — picks the correct tick/untick value instead of guessing a code name."
            checked={defaultIsCheckbox}
            onChange={(event) => {
              const checked = (event.target as HTMLInputElement).checked
              setDefaultIsCheckbox(checked)
              setDefaultIsMergeField(false)
              setValue('defaultData', '')
            }}
          />
          {defaultIsMergeField ? (
            <Select
              label="Merge Field"
              options={mergeFieldOptions}
              value={mergeFieldOptions.find((option) => option.value === watch('defaultData')) ?? null}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => setValue('defaultData', option?.value ?? '')}
              errorMessage={errors.defaultData?.message}
            />
          ) : defaultIsCheckbox ? (
            <Select
              label="Default Value"
              options={CHECKBOX_VALUE_OPTIONS}
              value={CHECKBOX_VALUE_OPTIONS.find((option) => option.value === watch('defaultData')) ?? null}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => setValue('defaultData', option?.value ?? '')}
              errorMessage={errors.defaultData?.message}
            />
          ) : (
            <InputGroup
              label="Default Value"
              labelInformationTooltip="A static value written to the PDF field. When a form field is linked above, this only applies together with the condition below (e.g. ticking a checkbox)."
              {...register('defaultData')}
              errorMessage={errors.defaultData?.message}
            />
          )}

          <Checkbox
            label="Apply Default Value Conditionally"
            supplementaryInfo="Only write the Default Value when a condition is met — e.g. tick a checkbox only when a radio answer matches a specific choice."
            checked={isConditional}
            onChange={() => setValue('isConditional', !isConditional)}
          />
          {isConditional && (
            <>
              {isLinked ? (
                <FieldHelp>Compares against the linked form field&apos;s own answer.</FieldHelp>
              ) : (
                <>
                  <Checkbox
                    label="Condition is a merge field"
                    checked={conditionIsMergeField}
                    onChange={(event) => {
                      const checked = (event.target as HTMLInputElement).checked
                      setConditionIsMergeField(checked)
                      setValue('conditionalData', '')
                    }}
                  />
                  {conditionIsMergeField ? (
                    <Select
                      label="Condition Merge Field"
                      options={mergeFieldOptions}
                      value={mergeFieldOptions.find((option) => option.value === watch('conditionalData')) ?? null}
                      getOptionLabel={(option: EnumOption) => option.label}
                      getOptionValue={(option: EnumOption) => option.value}
                      onChange={(option: EnumOption | null) => setValue('conditionalData', option?.value ?? '')}
                      errorMessage={errors.conditionalData?.message}
                    />
                  ) : (
                    <InputGroup
                      label="Condition Value/Token"
                      labelInformationTooltip="Usually a single merge token, but you can type more than one token here (e.g. two tokens back to back) if the condition needs to compare against combined text."
                      {...register('conditionalData')}
                      errorMessage={errors.conditionalData?.message}
                    />
                  )}
                </>
              )}

              {isLinked && linkedFieldOption.editType === 'Checkbox' ? (
                // A linked checkbox's own answer can only ever be true/false — no need to ask, just offer it.
                <Select
                  label="Value to Match"
                  options={YES_NO_VALUE_OPTIONS}
                  value={YES_NO_VALUE_OPTIONS.find((option) => option.value === watch('conditionalValue')) ?? null}
                  getOptionLabel={(option: EnumOption) => option.label}
                  getOptionValue={(option: EnumOption) => option.value}
                  onChange={(option: EnumOption | null) => setValue('conditionalValue', option?.value ?? '')}
                  errorMessage={errors.conditionalValue?.message}
                />
              ) : isLinked && linkedFieldOption.answerOptions && linkedFieldOption.answerOptions.length > 0 ? (
                // A linked dropdown/radio/checkbox-group's own answer can only ever be one of its own
                // options — offer exactly that set instead of a generic enum catalog or free text.
                <Select
                  label="Value to Match"
                  options={linkedFieldOption.answerOptions}
                  value={
                    linkedFieldOption.answerOptions.find((option) => option.value === watch('conditionalValue')) ?? null
                  }
                  getOptionLabel={(option: EnumOption) => option.label}
                  getOptionValue={(option: EnumOption) => option.value}
                  onChange={(option: EnumOption | null) => setValue('conditionalValue', option?.value ?? '')}
                  errorMessage={errors.conditionalValue?.message}
                />
              ) : (
                <>
                  <Checkbox
                    label="Value to Match is an enum value"
                    supplementaryInfo="Turn on if the merge token above resolves to a value backed by an enum (e.g. a payment frequency) — pick it below instead of typing its display text from memory."
                    checked={matchIsEnumValue}
                    onChange={(event) => {
                      const checked = (event.target as HTMLInputElement).checked
                      setMatchIsEnumValue(checked)
                      setMatchIsYesNo(false)
                      setValue('conditionalValue', '')
                    }}
                  />
                  <Checkbox
                    label="Value to Match is Yes/No"
                    supplementaryInfo="Turn on if the merge token above resolves to a Yes/No value — picks the correct value instead of typing true/false from memory."
                    checked={matchIsYesNo}
                    onChange={(event) => {
                      const checked = (event.target as HTMLInputElement).checked
                      setMatchIsYesNo(checked)
                      setMatchIsEnumValue(false)
                      setValue('conditionalValue', '')
                    }}
                  />
                  {matchIsEnumValue ? (
                    <Select
                      label="Value to Match"
                      options={enumValueOptions}
                      value={enumValueOptions.find((option) => option.value === watch('conditionalValue')) ?? null}
                      getOptionLabel={(option: EnumOption) => option.label}
                      getOptionValue={(option: EnumOption) => option.value}
                      onChange={(option: EnumOption | null) => setValue('conditionalValue', option?.value ?? '')}
                      errorMessage={errors.conditionalValue?.message}
                    />
                  ) : matchIsYesNo ? (
                    <Select
                      label="Value to Match"
                      options={YES_NO_VALUE_OPTIONS}
                      value={YES_NO_VALUE_OPTIONS.find((option) => option.value === watch('conditionalValue')) ?? null}
                      getOptionLabel={(option: EnumOption) => option.label}
                      getOptionValue={(option: EnumOption) => option.value}
                      onChange={(option: EnumOption | null) => setValue('conditionalValue', option?.value ?? '')}
                      errorMessage={errors.conditionalValue?.message}
                    />
                  ) : (
                    <InputGroup
                      label="Value to Match"
                      labelInformationTooltip="The Default Value above is applied only when the compared value equals this."
                      {...register('conditionalValue')}
                      errorMessage={errors.conditionalValue?.message}
                    />
                  )}
                </>
              )}
            </>
          )}

          <Checkbox
            label="Recipient Field"
            supplementaryInfo="Resolved once per recipient, repeating the PDF field key with a {{idx}} placeholder."
            checked={isRecipientField}
            onChange={() => setValue('isRecipientField', !isRecipientField)}
          />
          {isRecipientField && (
            <Checkbox
              label="Primary Recipient Only"
              checked={Boolean(watch('primaryOnly'))}
              onChange={() => setValue('primaryOnly', !watch('primaryOnly'))}
            />
          )}
          <Checkbox
            label="Exclude From Preview"
            checked={Boolean(watch('excludeFromPreview'))}
            onChange={() => setValue('excludeFromPreview', !watch('excludeFromPreview'))}
          />
          <Checkbox
            label="Redactable"
            checked={Boolean(watch('redactable'))}
            onChange={() => setValue('redactable', !watch('redactable'))}
          />
        </FieldStack>
      )}
    </Drawer>
  )
}
